import { useEffect, useLayoutEffect, useState } from "react";

import PropTypes from "prop-types";

import getLevelDisplayValues from "@/assignments/utils/getLevelDisplayValues";
import { NUMERICAL_GRADE_BANDS } from "@/constants/GradeAndLexileLevels";
import { LANG_TOGGLE_LIST } from "@/constants/Language";
import getCurrentLevelByNumericGrade from "@/utils/getCurrentLevelByNumericGrade";
import ActionsDropdown, { DROPDOWN_CLOSE_DELAY } from "@/view/ActionsDropdown";
import LevelDropdownItems from "@/view/LevelSelection/LevelDropdownItems";
import LevelItem from "@/view/LevelSelection/LevelItem";
import { buttonCss, buttonContentsCss, popOutCss } from "./LevelDropdown.style";

const propTypes = {
  currentContentLevel: PropTypes.object,
  defaultReadingLevel: PropTypes.number,
  eventProperties: PropTypes.object,
  language: PropTypes.oneOf(LANG_TOGGLE_LIST),
  levels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      grade_band: PropTypes.string,
    }),
  ),
  onDefaultReadingLevelChange: PropTypes.func,
  setContentLevel: PropTypes.func,
};

const NEWSELA_RECOMMENDED = "Newsela recommended";

const LevelDropdown = ({
  currentContentLevel,
  defaultReadingLevel,
  eventProperties,
  language,
  levels,
  onDefaultReadingLevelChange,
  setContentLevel,
}) => {
  const currentLevel = getCurrentLevelByNumericGrade(
    levels,
    defaultReadingLevel,
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { formattedGrade, lexileRange } = getLevelDisplayValues(currentLevel);
  const formattedLevelForAria = currentLevel
    ? `${formattedGrade} ${lexileRange}`
    : NEWSELA_RECOMMENDED;

  const onClickDropdownItem = (level, numericGrade, closeDropdown) => {
    closeDropdown();
    onDefaultReadingLevelChange(numericGrade);
    // The second state setter needs to be delayed to allow time for the popout to close.
    // Without this delay, the setter will trigger second re-render.
    // This will interrupt the dropdown closing animation.
    setTimeout(() => {
      if (level) {
        setContentLevel(level);
      }
    }, DROPDOWN_CLOSE_DELAY);
  };

  const renderDropdownItems = (closeDropdown) => (
    <LevelDropdownItems
      defaultReadingLevel={defaultReadingLevel}
      language={language}
      levels={levels}
      eventProperties={eventProperties}
      onClick={(level, numericGrade) =>
        onClickDropdownItem(level, numericGrade, closeDropdown)
      }
    />
  );

  const renderButtonContents = () => <LevelItem level={currentLevel} />;

  const dropdownButtonEventProperties = {
    ...eventProperties,
    actionPrefix: "Initiate",
    streamProperties: {
      ...eventProperties.streamProperties,
      description: "Open default reading level dropdown",
    },
  };

  // We're keeping track of the initial render so the initial level could be set in the
  // level selector. Once that has mounted, we can listen for when currentContentLevel
  // changes, and set the new value in the selector to that level.
  useLayoutEffect(() => {
    if (!isInitialLoad) {
      const currentReadingLevel =
        NUMERICAL_GRADE_BANDS[currentContentLevel?.grade_band];
      onDefaultReadingLevelChange(currentReadingLevel);
    }
  }, [currentContentLevel]);

  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  return (
    <ActionsDropdown
      ariaProps={{
        "aria-label": `${formattedLevelForAria} lexile level, select to change level`,
      }}
      buttonText={renderButtonContents()}
      buttonCss={buttonCss}
      buttonContentsCss={buttonContentsCss(
        formattedLevelForAria === NEWSELA_RECOMMENDED,
      )}
      dataProps={{ "data-qa-selector": "assignment-level-selector" }}
      eventProperties={dropdownButtonEventProperties}
      id="assignment-level-selector"
      popOutCss={popOutCss}
      renderDropdownItems={renderDropdownItems}
    />
  );
};

LevelDropdown.propTypes = propTypes;

export default LevelDropdown;
